import { useRouter } from 'next/router';
import {
	BUILD_FLOW_STEPS,
	CUSTOMER_JOURNEYS,
	getBfLensesStepTitle,
	getBfPrescriptionsStepTitle,
	getBfProgressiveStepTitle,
	LOCALE_CODES,
} from '@constants';
import { Caption, EducationModal, Flex, Lenses, Prescriptions, PrescriptionsLensExpansion, Title } from '@components';
import { useBFContext } from '@context';
import { RX_TYPE } from '@utils/constants/base-skus';
import { useEducationTabs, useFeatureInLocale, useTranslation } from '@utils/index';
import styles from './PrescriptionStep.module.scss';

const getTranslatedTexts = translator => {
	return {
		selectAllThatApply: translator('select-all-that-apply'),
		eyeSafetyRecommendation: translator('eye-safety-recommendation'),
	};
};

const PrescriptionStep = () => {
	const { rxType, step, journey } = useBFContext();
	const { locale } = useRouter();
	const isCartUsability = useFeatureInLocale('is-cart-usability', LOCALE_CODES.US);
	const isLowerTierProgressive = useFeatureInLocale('is-lower-tier-progressive', LOCALE_CODES.US);
	const isSunLens = journey === CUSTOMER_JOURNEYS.SUNGLASSES;
	const isSubmitStep = step === BUILD_FLOW_STEPS.SUBMIT;
	const isCollectionsStep = step === BUILD_FLOW_STEPS.TOP_FRAMES;
	const isPackagesStep = step === BUILD_FLOW_STEPS.PACKAGE;
	const isLensesStep = step === BUILD_FLOW_STEPS.CUSTOMIZE;
	const isProgressiveStep = step === BUILD_FLOW_STEPS.PROGRESSIVE;
	const isPrescriptionsStep = step === BUILD_FLOW_STEPS.LENS || isPackagesStep || isLensesStep || isProgressiveStep;
	const isReadersSelected = rxType === RX_TYPE.READERS;
	const shouldDisplayLenses = ((isLensesStep && isSunLens) || isCollectionsStep) && (isReadersSelected || !isSunLens);
	const { translator } = useTranslation();
	const translations = getTranslatedTexts(translator);
	const INFO_TAB_DESCRIPTION_COPY = translations.eyeSafetyRecommendation;

	const { educationModalProps } = useEducationTabs({ isSunglassJourney: isSunLens, type: step });

	return (
		<div className={styles.container}>
			{
				<Flex align='end' justify='between' gap={3} className={styles.title}>
					<Flex column>
						<Title
							style={{
								opacity:
									step !== BUILD_FLOW_STEPS.LENS && !isSunLens && step !== BUILD_FLOW_STEPS.PROGRESSIVE
										? 0.5
										: 1,
								fontSize: '1.4rem',
							}}
						>
							{isLowerTierProgressive && step === BUILD_FLOW_STEPS.PROGRESSIVE
								? getBfProgressiveStepTitle(locale)
								: isPrescriptionsStep || isSubmitStep || isSunLens
									? isLowerTierProgressive
										? getBfPrescriptionsStepTitle(locale).replace('1.', '')
										: getBfPrescriptionsStepTitle(locale)
									: getBfLensesStepTitle(locale)}
						</Title>
						{!(isPrescriptionsStep || isSunLens) && !isSubmitStep && (
							<Caption small>({translations.selectAllThatApply})</Caption>
						)}
					</Flex>
					{isPrescriptionsStep && educationModalProps ? (
						<div onClick={e => e.stopPropagation()}>
							<EducationModal
								tab={0}
								infoCopy={step !== BUILD_FLOW_STEPS.PROGRESSIVE ? INFO_TAB_DESCRIPTION_COPY : null}
								{...educationModalProps}
							/>
						</div>
					) : null}
				</Flex>
			}
			<div className={styles['options-container']}>
				{isSunLens ? (
					<>
						<Prescriptions />
						{shouldDisplayLenses && <Lenses />}
					</>
				) : (
					<PrescriptionsLensExpansion isCartUsability={isCartUsability} />
				)}
			</div>
		</div>
	);
};

export default PrescriptionStep;
